import type { ReactElement } from "react";
import { ToggleButton, ToggleButtonGroup } from "react-aria-components";
import { toggleChipGroupStyle } from "./style.js";

export interface ToggleChipOption<T extends string> {
  readonly id: T;
  readonly label: string;
}

export interface ToggleChipGroupProps<T extends string> {
  /** The options rendered as chips, in display order. */
  readonly options: readonly ToggleChipOption<T>[];
  /** The currently selected option ids. */
  readonly value: readonly T[];
  /** Called with the next selection (in `options` order) on every toggle. */
  readonly onChange: (value: readonly T[]) => void;
  /** Accessible label for the group; required so screen readers can name it. */
  readonly "aria-label": string;
  readonly isDisabled?: boolean;
}

/**
 * A multi-select toggle group rendered as a row of chips. Wraps react-aria's
 * ToggleButtonGroup so keyboard and screen-reader semantics come for free, and
 * takes its look from the shared {@link toggleChipGroupStyle}.
 *
 * The component allows an empty selection; callers that require at least one
 * selected chip (e.g. a weekday schedule) should enforce that in `onChange`.
 */
export function ToggleChipGroup<T extends string>({
  options,
  value,
  onChange,
  isDisabled,
  "aria-label": ariaLabel,
}: ToggleChipGroupProps<T>): ReactElement {
  const style = toggleChipGroupStyle();
  return (
    <ToggleButtonGroup
      selectionMode="multiple"
      selectedKeys={value}
      isDisabled={isDisabled}
      aria-label={ariaLabel}
      onSelectionChange={(keys) => {
        // Report the selection in the caller's option order rather than the
        // set's insertion order, so the value stays stable as chips toggle.
        onChange(
          options.map((option) => option.id).filter((id) => keys.has(id)),
        );
      }}
      className={style.group()}
    >
      {options.map((option) => (
        <ToggleButton key={option.id} id={option.id} className={style.button()}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
